import { useParams, Link } from 'react-router-dom';
import Seo from '../../components/Seo';
import { PageLoader, ErrorState, Breadcrumbs } from '../../components/ui';
import { useAsync } from '../../lib/useAsync';
import { sanitize } from '../../lib/sanitize';
import { storiesApi } from '../../api/resources';
import { assetUrl } from '../../api/client';
import { formatDate } from '../../lib/format';

function ShareBar({ title }) {
  const url = typeof window !== 'undefined' ? window.location.href : '';
  const enc = encodeURIComponent;
  const links = [
    { label: 'Facebook', href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}` },
    { label: 'X', href: `https://twitter.com/intent/tweet?url=${enc(url)}&text=${enc(title)}` },
    { label: 'LinkedIn', href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}` },
    { label: 'Email', href: `mailto:?subject=${enc(title)}&body=${enc(url)}` },
  ];
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm font-medium text-ink-muted">Share:</span>
      {links.map((l) => (
        <a
          key={l.label}
          href={l.href}
          target="_blank"
          rel="noopener noreferrer"
          className="pill-maroon transition-colors hover:bg-maroon-100"
        >
          {l.label}
        </a>
      ))}
      <button
        onClick={() => navigator.clipboard?.writeText(url)}
        className="pill bg-surface-gray text-ink-soft transition-colors hover:bg-cream-300"
      >
        Copy link
      </button>
    </div>
  );
}

export default function StoryDetail() {
  const { id } = useParams();
  const { data: story, loading, error, reload } = useAsync(() => storiesApi.get(id), [id]);

  if (loading) return <PageLoader label="Loading story…" />;
  if (error) return <div className="container-page py-16"><ErrorState error={error} onRetry={reload} /></div>;
  if (!story) return null;

  const img = assetUrl(story.featured_image_path);
  const banner = img || '/img/story-a.jpg';
  console.log('StoryDetail: story', story);
  return (
    <>
      <Seo
        title={story.title}
        description={story.excerpt}
        image={img}
        type="article"
      />

      <article className="pb-16">
        <header className="relative overflow-hidden">
          <img
            src={banner}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="hero-overlay" />
          <div className="container-page relative max-w-4xl py-20 sm:py-28">
            <div className="mb-4 flex flex-wrap items-center gap-3">
              {story.category && <span className="pill-gold">{story.category.name}</span>}
              {story.published_at && (
                <span className="text-sm text-cream-200">{formatDate(story.published_at)}</span>
              )}
            </div>
            <h1 className="font-serif text-3xl font-bold leading-tight text-white sm:text-5xl">
              {story.title}
            </h1>
            {story.author && (
              <p className="mt-4 text-sm text-cream-200">
                By <span className="font-semibold">{story.author.name}</span>
              </p>
            )}
          </div>
        </header>

        <div className="container-page max-w-3xl pt-8">
          <Breadcrumbs
            items={[{ label: 'Home', to: '/' }, { label: 'Stories', to: '/stories' }, { label: story.title }]}
          />

          {story.excerpt && (
            <div className="mt-6 rounded-2xl bg-cream-100 p-6 ring-1 ring-cream-300">
              <span aria-hidden="true" className="font-display text-3xl leading-none text-gold-500">
                “
              </span>
              <p className="mt-1 text-lg italic leading-relaxed text-ink-soft">{story.excerpt}</p>
            </div>
          )}

          <div
            className="rich-content mt-6"
            dangerouslySetInnerHTML={{ __html: sanitize(story.body) || '<p>No content.</p>' }}
          />

          <hr className="my-8 border-cream-300" />
          <ShareBar title={story.title} />

          <div className="mt-10">
            <Link to="/stories" className="btn-ghost">
              ← Back to all stories
            </Link>
          </div>
        </div>
      </article>
    </>
  );
}
